import { useEffect, useRef, useState } from 'react';
import { api, ApiError, call, type Profile } from '../api';
import { useApp } from '../context';
import { useIsOwner } from '../owner';

/**
 * Avatar van een profiel (zoals Xperience): een eigen foto (hier rond bijsnijden) of een acteur uit TMDB.
 * Geldt op alle apparaten. Je eigen profiel altijd; de beheerder (of het hoofdprofiel) ook die van anderen.
 */
export function AvatarSettings() {
  const { me } = useApp();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [target, setTarget] = useState(me.profile?.uuid ?? '');
  const [current, setCurrent] = useState<string>(me.profile?.thumb ?? '');
  const [file, setFile] = useState<HTMLImageElement | null>(null);
  const [zoom, setZoom] = useState(1);
  const [pos, setPos] = useState({ x: 0.5, y: 0.5 });
  const [q, setQ] = useState('');
  const [people, setPeople] = useState<{ id: number; name: string; photo: string }[] | null>(null);
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    api.profiles().then((r) => setProfiles(r.profiles)).catch(() => setProfiles([]));
  }, []);
  useEffect(() => {
    const p = profiles.find((x) => x.uuid === target);
    if (p) setCurrent(p.thumb);
  }, [target, profiles]);

  // Bijsnijden: vierkant uit het midden (met zoom en verschuiven), 256×256
  useEffect(() => {
    const c = canvas.current;
    if (!c || !file) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    const side = Math.min(file.naturalWidth, file.naturalHeight) / zoom;
    const sx = (file.naturalWidth - side) * pos.x;
    const sy = (file.naturalHeight - side) * pos.y;
    ctx.clearRect(0, 0, 256, 256);
    ctx.drawImage(file, sx, sy, side, side, 0, 0, 256, 256);
  }, [file, zoom, pos]);

  const pick = (f: File) => {
    const url = URL.createObjectURL(f);
    const im = new Image();
    im.onload = () => { setFile(im); setZoom(1); setPos({ x: 0.5, y: 0.5 }); };
    im.onerror = () => setMsg({ text: 'Dit bestand is geen plaatje.', error: true });
    im.src = url;
  };

  const save = async (body: { dataUrl?: string; url?: string }) => {
    setMsg(null);
    try {
      const r = await call<{ thumb: string }>(`/api/avatars/${encodeURIComponent(target)}`, { method: 'PUT', body: JSON.stringify(body) });
      setCurrent(r.thumb);
      setProfiles((ps) => ps.map((p) => (p.uuid === target ? { ...p, thumb: r.thumb } : p)));
      setFile(null);
      setMsg({ text: 'Avatar opgeslagen. In het menu zie je hem na opnieuw laden.' });
    } catch (err) {
      setMsg({ text: err instanceof ApiError && err.message !== err.code ? err.message : 'Opslaan mislukt.', error: true });
    }
  };

  const remove = async () => {
    await call(`/api/avatars/${encodeURIComponent(target)}`, { method: 'DELETE' }).catch(() => undefined);
    const r = await api.profiles().catch(() => null);
    if (r) setProfiles(r.profiles);
    setMsg({ text: 'Eigen avatar weggehaald.' });
  };

  const search = async () => {
    const r = await call<{ people: { id: number; name: string; photo: string }[] }>(`/api/avatars/people?q=${encodeURIComponent(q.trim())}`).catch(() => ({ people: [] }));
    setPeople(r.people);
  };

  const owner = useIsOwner();
  const editable = !!owner && profiles.length > 1;

  return (
    <section className="settings-block avatar-settings" aria-label="Avatar">
      <h2>Avatar</h2>
      <p className="subtle hint">Een eigen foto of een acteur als profielfoto. Geldt op alle apparaten.</p>
      {editable && (
        <label className="ua2-select">Voor profiel
          <select value={target} onChange={(e) => { setTarget(e.target.value); setFile(null); setMsg(null); }}>
            {profiles.map((p) => <option key={p.uuid} value={p.uuid}>{p.title}{p.uuid === me.profile?.uuid ? ' (jij)' : ''}</option>)}
          </select>
        </label>
      )}

      <div className="av-row">
        <span className="av-current">{current ? <img src={current} alt="Huidige avatar" /> : null}</span>
        <div className="av-actions">
          <button className="btn btn-sm" onClick={() => input.current?.click()}>Foto kiezen…</button>
          <button className="btn btn-ghost btn-sm" onClick={() => void remove()}>Eigen avatar weghalen</button>
          <input ref={input} type="file" accept="image/*" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) pick(f); e.target.value = ''; }} />
        </div>
      </div>

      {file && (
        <div className="av-crop">
          <canvas ref={canvas} width={256} height={256} className="av-canvas" aria-label="Voorbeeld van de bijgesneden foto" />
          <div className="av-sliders">
            <label>Inzoomen <input type="range" min={1} max={3} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} /></label>
            <label>Links / rechts <input type="range" min={0} max={1} step={0.01} value={pos.x} onChange={(e) => setPos((p) => ({ ...p, x: Number(e.target.value) }))} /></label>
            <label>Boven / onder <input type="range" min={0} max={1} step={0.01} value={pos.y} onChange={(e) => setPos((p) => ({ ...p, y: Number(e.target.value) }))} /></label>
            <div className="form-actions">
              <button className="btn btn-primary btn-sm" onClick={() => { const d = canvas.current?.toDataURL('image/jpeg', 0.85); if (d) void save({ dataUrl: d }); }}>Gebruiken</button>
              <button className="btn btn-ghost btn-sm" onClick={() => setFile(null)}>Annuleren</button>
            </div>
          </div>
        </div>
      )}

      <h3 className="bk-sub">Of kies een acteur</h3>
      <form className="bk-form" onSubmit={(e) => { e.preventDefault(); void search(); }}>
        <input className="text-input" placeholder="Zoek een acteur, bijv. Pedro Pascal" value={q} onChange={(e) => setQ(e.target.value)} />
        <button className="btn btn-sm" type="submit">{q.trim() ? 'Zoeken' : 'Populair'}</button>
      </form>
      {people && (
        <div className="av-grid" role="list">
          {people.length === 0 && <p className="subtle">Niemand gevonden.</p>}
          {people.map((p) => (
            <button key={p.id} role="listitem" className="av-person" onClick={() => void save({ url: p.photo })} title={`${p.name} als avatar`}>
              <img src={p.photo} alt="" loading="lazy" />
              <span>{p.name}</span>
            </button>
          ))}
        </div>
      )}
      {msg && <p className={msg.error ? 'error' : 'subtle'} role="status">{msg.text}</p>}
    </section>
  );
}
